import { ShieldCheck, GraduationCap, Wallet, Stethoscope } from 'lucide-react';
import StudentVisaGuideShell from './components/StudentVisaGuideShell';

/**
 * Australia Student Visa (Subclass 500) — config object consumed by the
 * shared StudentVisaGuideShell. All rendering logic lives in the shell.
 * Edit data here; never touch the shell unless adding a new section type.
 */

const CONFIG = {
  testidPrefix: 'au-visa',

  seo: {
    title: 'Australia Student Visa (Subclass 500) Guide 2026: No-Fail Roadmap',
    description: 'Australia Subclass 500 Student Visa 2026 for Indians: AUD 29,710 financial proof, Genuine Student (GS) requirement, OSHC, document checklist & 8-step process.',
  },

  breadcrumb: [
    { name: 'Home', url: 'https://zafco.in/' },
    { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
    { name: 'Australia', url: 'https://zafco.in/study-abroad/australia/' },
    { name: 'Student Visa Guide' },
  ],

  hero: {
    eyebrow: 'Australia Visa Guide · 2026',
    lastUpdated: '28 Feb 2026',
    titlePre: 'Australia Subclass 500 Visa',
    titleAccent: '2026',
    summaryHtml: 'The complete no-fail roadmap for Indian students with a Confirmation of Enrolment from a CRICOS-registered provider. Updated for 2026 — including the <strong>AUD 29,710</strong> living-cost requirement, the <strong>Genuine Student (GS)</strong> framework, and the <strong>AUD 1,600</strong> visa fee.',
    primaryCta: 'Book Free Visa Counselling',
    primaryHref: '/study-abroad/book-counselling/?source=study-abroad-australia-student-visa',
    secondaryCta: 'Evaluate My Profile',
    secondaryHref: '/study-abroad/free-profile-evaluation/?source=study-abroad-australia-student-visa-hero&country=australia',
  },

  snapshot: {
    eyebrow: '2026 Living-Cost Requirement',
    amount: 'AUD 29,710',
    subLabel: '12-month primary applicant',
    paragraphsHtml: [
      '<strong class="text-amber-900">The single most scrutinised figure in your Subclass 500.</strong> The old AUD 24,505 amount is obsolete — the Department of Home Affairs requires <strong>AUD 29,710</strong> for 2026 primary applicants, refreshed annually.',
      'Add <strong>+AUD 10,394</strong> for spouse, <strong>+AUD 4,449</strong> per child, plus first-year tuition and OSHC. Your sponsor must demonstrate the entire amount in liquid form.',
    ],
  },

  timeline: {
    heading: { eyebrow: 'When to Start', title: 'Eligibility & Timeline Matrix', subtitle: 'Backwards-plan from your intake date — CoE issuance + visa processing alone can consume 5–7 weeks.' },
    rows: [
      { window: '6 months before intake', action: 'Receive Confirmation of Enrolment (CoE) from your CRICOS provider.' },
      { window: '4–5 months before',       action: 'Pay OSHC premium + arrange financial-evidence (AUD 29,710 + tuition + travel).' },
      { window: '3–4 months before',       action: 'Sit IELTS/PTE/TOEFL and finalise the Genuine Student (GS) statement.' },
      { window: '2–3 months before',       action: 'Lodge Subclass 500 online via ImmiAccount; pay AUD 1,600 visa fee.' },
      { window: '1–2 months before',       action: 'Biometrics at VFS + health examination (Bupa-panel hospitals).' },
      { window: '4–8 weeks after',         action: 'Visa processing window — 75% of Indian applicants in 2026 cleared in 5–7 weeks.' },
    ],
  },

  financialProof: {
    heading: { eyebrow: 'Money in the Bank', title: 'Financial Evidence Breakdown', subtitle: 'The Department wants to see total liquid funds. Spread parental FDs, education-loan sanction letters, and 6-month sponsor statements across multiple sources.' },
    rows: [
      { item: '12 months living costs (primary applicant)', amount: 'AUD 29,710',           note: 'Reset annually by the Department of Home Affairs.' },
      { item: 'Tuition fee for the first year',              amount: 'AUD 22,000 – 45,000',  note: 'Course & university dependent.' },
      { item: 'OSHC health insurance (single, full course)', amount: 'AUD 500 – 700 / year', note: 'Mandatory for the entire visa duration.' },
      { item: 'Return travel allowance',                     amount: 'AUD 2,500',            note: 'Department-suggested baseline.' },
      { item: 'Spouse (if accompanying)',                    amount: '+ AUD 10,394',         note: 'Add to primary applicant total.' },
      { item: 'Each child (if accompanying)',                amount: '+ AUD 4,449',          note: 'Plus AUD 13,502 / year school fees.' },
    ],
    cta: {
      tag: 'Financial Setup',
      heading: 'Build a Bullet-Proof Financial File',
      copy: 'ZAFCO students get a tailored AUD-funds plan — parental FDs, education loan structure, sponsor affidavits — designed to satisfy GS scrutiny.',
      button: 'Get Financial Setup Support',
      href: '/study-abroad/book-counselling/?source=study-abroad-australia-student-visa-cta-financial',
      testid: 'au-visa-cta-financial',
    },
  },

  policyAlerts: {
    heading: { eyebrow: 'Critical 2024–26 Policy Changes', title: 'What Has Changed Since 2023', subtitle: 'Two policy shifts dramatically affect your application strategy. Both are non-negotiable for 2026 intakes.' },
    items: [
      { date: '23 Mar 2024', title: 'Genuine Student (GS) Replaces GTE',
        body: 'Migration Direction 107 introduced the GS framework. Visa officers now assess your "genuine intent to study" via a structured 4-question online module + supporting evidence — much stricter than the old GTE statement.' },
      { date: '1 Oct 2024',  title: 'Visa Fee Doubled to AUD 1,600',
        body: 'The Subclass 500 lodgement charge increased from AUD 710 → AUD 1,600 — Australia is now the most expensive student-visa application in the OECD. Budget accordingly.' },
    ],
  },

  checklists: {
    heading: { eyebrow: 'Zero-Error Edition', title: 'The Complete Document Checklist', subtitle: 'Four categories. Upload PDFs ≤ 5 MB each into your ImmiAccount. Disorganised folders trigger info-request emails that add 2–3 weeks.' },
    cards: [
      { tag: 'Identity', Icon: ShieldCheck, tone: 'blue', items: [
        'Valid passport — ≥6 months validity beyond intended stay',
        'Recent passport-size photo (digital + 2 print copies)',
        'Birth certificate / 10th-grade marksheet for date-of-birth proof',
        'National ID (Aadhaar) — non-mandatory but speeds verification',
      ]},
      { tag: 'Academic', Icon: GraduationCap, tone: 'gold', items: [
        'Confirmation of Enrolment (CoE) from a CRICOS-registered provider',
        'Academic transcripts (10th, 12th, Bachelor\u2019s — apostilled)',
        'English proficiency — IELTS 6.0+ / PTE 50+ / TOEFL iBT 64+',
        'Statement of Purpose tailored to the Genuine Student (GS) framework',
        'Letters of Recommendation (2 — academic / professional)',
      ]},
      { tag: 'Financial', Icon: Wallet, tone: 'emerald', items: [
        'AUD 29,710 living-cost proof (FD / parental fixed deposit / education loan sanction)',
        'Tuition fee receipt (first-year minimum already paid OR loan disbursement schedule)',
        'OSHC payment receipt covering the full visa duration',
        '6-month sponsor bank statements + income tax returns (3 years)',
        'Affidavit of sponsorship (notarised) if parents are sponsoring',
      ]},
      { tag: 'Health+GS', Icon: Stethoscope, tone: 'amber', items: [
        'OSHC health insurance certificate (mandatory)',
        'Medical examination via Bupa-panel hospital (post-visa lodgement)',
        'Police clearance certificate (PCC) — if requested',
        'GS / Genuine Student declaration aligned with Migration Direction 107',
      ]},
    ],
  },

  providers: {
    heading: { eyebrow: 'OSHC Deep-Dive', title: 'Health-Cover Provider Comparison 2026', subtitle: 'All four are Department-approved. Choose by hospital network, claim turnaround, or annual premium.' },
    columns: [
      { key: 'name',         label: 'Provider',       bold: true },
      { key: 'single',       label: 'Single' },
      { key: 'couples',      label: 'Couples' },
      { key: 'familyOption', label: 'Family Option',  hide: 'hidden md:table-cell' },
      { key: 'verdict',      label: 'Verdict',        hide: 'hidden lg:table-cell' },
    ],
    rows: [
      { name: 'Bupa Australia',   single: 'AUD 615 / yr', couples: 'AUD 3,170 / yr', familyOption: 'Yes', verdict: 'Largest hospital network · embassy-default' },
      { name: 'Allianz Care',     single: 'AUD 580 / yr', couples: 'AUD 2,990 / yr', familyOption: 'Yes', verdict: 'Lowest annual cost (single)' },
      { name: 'Medibank',         single: 'AUD 640 / yr', couples: 'AUD 3,250 / yr', familyOption: 'Yes', verdict: 'Best app + telehealth access' },
      { name: 'NIB Health Funds', single: 'AUD 590 / yr', couples: 'AUD 3,050 / yr', familyOption: 'Yes', verdict: 'Lowest claim turnaround (~3 days)' },
    ],
  },

  steps: {
    heading: { eyebrow: 'Process Flow', title: 'The 8-Step Subclass 500 Application', subtitle: 'Linear, accountable, and tracked. Each step has a single deliverable — no parallel scrambling.' },
    items: [
      { title: 'Accept CoE',           desc: 'Pay tuition deposit and download the official Confirmation of Enrolment (CoE) PDF from your CRICOS provider.' },
      { title: 'Purchase OSHC',        desc: 'Buy OSHC covering the full course + 1 month buffer. Save the receipt for the visa lodgement.' },
      { title: 'Prepare GS Statement', desc: 'Draft the Genuine Student narrative — ties to home country, course rationale, post-study career plan (1,000–1,500 words).' },
      { title: 'Create ImmiAccount',   desc: 'Register a personal ImmiAccount on the Home Affairs portal and start the Subclass 500 form.' },
      { title: 'Pay Visa Charge',      desc: 'Pay the AUD 1,600 Visa Application Charge (VAC). Keep the transaction ID.' },
      { title: 'Lodge & Upload Docs',  desc: 'Submit the application and upload the full checklist as PDFs (≤5 MB each).' },
      { title: 'Biometrics + Health',  desc: 'Attend VFS for biometrics and complete the health examination at a Bupa-panel clinic.' },
      { title: 'Await Decision',       desc: 'Median India processing 5–7 weeks for 2026. Monitor your ImmiAccount dashboard daily.' },
    ],
  },

  interview: {
    heading: { eyebrow: 'Genuine Student Interview', title: '5 Questions You Will Be Asked', subtitle: 'Practise answers that are specific, not memorised. Officers note hesitation.' },
    questions: [
      'Why did you choose Australia over Canada / UK / US?',
      'Why this specific CRICOS provider and course?',
      'Who is sponsoring you and what is their occupation / annual income?',
      'What is your career plan after graduation — in India or Australia?',
      'How will the AUD 29,710 in your account be utilised over 12 months?',
    ],
  },

  postArrival: {
    heading: { eyebrow: 'After Landing', title: 'First-30-Days Checklist', subtitle: 'The 5 items every new arrival must complete to stay compliant and well-positioned for the Subclass 485.' },
    items: [
      'Activate your Tax File Number (TFN) within 30 days of arrival.',
      'Open an Australian bank account (Commonwealth / NAB / ANZ student account).',
      'Register with your university\u2019s international office — orientation is mandatory.',
      'Verify your work permit — 48 hours / fortnight during semester, unlimited during break.',
      'Apply for the Post-Study Work Visa (Subclass 485) before your Subclass 500 expires.',
    ],
  },

  faqsHeading: { eyebrow: 'Frequently Asked', title: 'Subclass 500 FAQs', subtitle: 'Schema-marked answers to the 8 most-asked questions on funds, GS, work rights and post-study options.' },
  faqs: [
    { q: 'How much money do I need to show for an Australia Student Visa in 2026?', a: 'AUD 29,710 for 12 months of living costs + first-year tuition + OSHC + AUD 2,500 travel. The AUD 29,710 figure is updated annually by the Department of Home Affairs — confirm before applying.' },
    { q: 'What is the new Genuine Student (GS) requirement?', a: 'Effective 23 March 2024, the GS requirement replaced the old GTE statement. You now answer a structured 4-question module covering your circumstances, course choice, post-study intent, and home-country ties. Migration Direction 107 governs how visa officers weight these answers.' },
    { q: 'How long does the Australia Student Visa take to process from India in 2026?', a: '5–7 weeks for 75% of Indian applicants. Complex cases (refusal history, course-mismatch) can extend to 3–4 months. No fast-track is officially available.' },
    { q: 'Can I work while on a Subclass 500 visa?', a: 'Yes — up to 48 hours per fortnight during semester (effective 1 July 2023 cap, up from 40), and unlimited hours during scheduled breaks. PhD students have no work restrictions.' },
    { q: 'Is OSHC mandatory?', a: 'Yes. Overseas Student Health Cover is mandatory for the entire visa duration. Bupa, Allianz Care, Medibank, and NIB are the four approved providers. Buy before visa lodgement.' },
    { q: 'What IELTS / PTE score do I need for the Subclass 500?', a: 'Minimum IELTS 6.0 (no band below 5.5) or PTE 50, but most CRICOS providers require IELTS 6.5+ for postgraduate courses. PhD applicants typically need IELTS 7.0+.' },
    { q: 'Can I extend my Subclass 500 from inside Australia?', a: 'Yes — apply onshore via ImmiAccount before your current visa expires. You\u2019ll need a new CoE (extension of programme or new course) and refreshed OSHC.' },
    { q: 'What is the Post-Study Work Visa (Subclass 485)?', a: 'Subclass 485 lets you stay 2–4 years post-graduation depending on degree level (2 yrs Bachelor\u2019s, 3 yrs Master\u2019s, 4 yrs PhD). Regional Australian study grants an extra 1–2 years.' },
  ],

  anchorLinks: {
    heading: { eyebrow: 'Explore Further', title: 'Related Australia Resources', subtitle: 'Course choice, cost of study, PR pathways, and the points calculator — everything in one cluster.' },
    items: [
      { label: 'Study in Australia',                       path: '/study-abroad/australia/' },
      { label: 'Australia Points Calculator',              path: '/australia-points-calculator' },
      { label: 'Cost of Studying in Australia',            path: '/study-abroad/australia/cost-of-study/' },
      { label: 'Best Universities in Australia',           path: '/study-abroad/australia/best-universities/' },
      { label: 'Australia PR after Study',                 path: '/study-abroad/australia/pr-after-study/' },
      { label: 'Free Profile Evaluation',                  path: '/study-abroad/free-profile-evaluation/?source=study-abroad-australia-student-visa-anchor' },
      { label: 'Book a Free Counselling Session',          path: '/study-abroad/book-counselling/?source=study-abroad-australia-student-visa-anchor' },
    ],
  },

  finalCta: {
    title: 'Lodge your Subclass 500 with zero surprises.',
    subtitle: 'Our Migration-Agent-supported counsellors run a 3-pass audit on every Australia file — GS narrative, financial structuring, document formatting — before lodgement.',
    whatsappText: 'Hello ZAFCO - I want help with Australia Subclass 500 visa.',
    counsellingSource: 'study-abroad-australia-student-visa-final-cta',
  },
};

const AustraliaStudentVisaPage = () => <StudentVisaGuideShell config={CONFIG} />;
export default AustraliaStudentVisaPage;
